import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { MOCK_COLIS } from './mockData';

// ─── Contexte ───────────────────────────────────────────────────────────────
export const AppContext = createContext(null);

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used inside ColisContext');
  return ctx;
}

// ─── Helpers ────────────────────────────────────────────────────────────────
async function parseApiResponse(res) {
  const contentType = res.headers.get('content-type') || '';
  const text = await res.text();
  let json = null;
  const trimmed = (text || '').trim();
  const looksLikeJson = trimmed.startsWith('{') || trimmed.startsWith('[');
  if (contentType.includes('application/json') || looksLikeJson) {
    try {
      json = trimmed ? JSON.parse(trimmed) : null;
    } catch {
      json = null;
    }
  }
  return { ok: res.ok, status: res.status, json, text };
}

function apiUrl(path) {
  const p = String(path || '').replace(/^\/+/, '');
  return `/api/index.php?__path__=${encodeURIComponent(p)}`;
}

// ─── Provider ───────────────────────────────────────────────────────────────
export default function ColisContext({ children }) {
  const [colis, setColis]   = useState([]);
  const [adminColis, setAdminColis] = useState([]);
  const [toast, setToast]   = useState(null);
  const [user, setUserState] = useState(null);
  const [authBooting, setAuthBooting] = useState(true);
  const [myColisIds, setMyColisIdsState] = useState([]);
  const [csrfToken, setCsrfToken] = useState('');
  const [clientTel, setClientTelState] = useState(() => {
    try {
      return localStorage.getItem('zaki_client_tel') || '';
    } catch {
      return '';
    }
  });

  // Toast helper
  const showToast = useCallback((message, type = 'info') => {
    setToast({ message, type });
  }, []);

  const dismissToast = useCallback(() => setToast(null), []);

  const setClientTel = useCallback((tel) => {
    const next = (tel || '').trim();
    setClientTelState(next);
    try {
      if (!next) localStorage.removeItem('zaki_client_tel');
      else localStorage.setItem('zaki_client_tel', next);
    } catch {
      // ignore storage errors
    }
  }, []);

  const fetchCsrf = useCallback(async () => {
    const res = await fetch(apiUrl('csrf'), {
      method: 'GET',
      credentials: 'include',
    });
    const out = await parseApiResponse(res);
    const token = out?.json?.csrfToken || '';
    if (token) setCsrfToken(token);
    return token;
  }, []);

  const apiRequest = useCallback(async (path, options = {}) => {
    const method = (options.method || 'GET').toUpperCase();
    const needsCsrf = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);

    let token = csrfToken;
    if (needsCsrf && !token) token = await fetchCsrf();

    const headers = { ...(options.headers || {}) };
    if (!headers['Accept']) headers['Accept'] = 'application/json';
    if (!headers['X-Requested-With']) headers['X-Requested-With'] = 'XMLHttpRequest';
    if (needsCsrf && token) headers['X-CSRF-TOKEN'] = token;

    const res = await fetch(apiUrl(path), {
      ...options,
      method,
      credentials: 'include',
      headers,
    });

    const out = await parseApiResponse(res);
    if (!out.ok) {
      const msg = out?.json?.message || out?.text || 'Erreur serveur';
      throw new Error(msg);
    }
    return out.json;
  }, [csrfToken, fetchCsrf]);

  const bootstrapAuth = useCallback(async () => {
    setAuthBooting(true);
    try {
      await fetchCsrf();
      const meJson = await apiRequest('me');
      const u = meJson?.user || null;
      setUserState(u);

      if (u) {
        const colisJson = await apiRequest('colis');
        const list = Array.isArray(colisJson?.colis) ? colisJson.colis : [];
        setColis(list);
        setMyColisIdsState(list.map(c => c.id));

        if (u?.is_admin) {
          const adminJson = await apiRequest('admin/colis');
          const adminList = Array.isArray(adminJson?.colis) ? adminJson.colis : [];
          setAdminColis(adminList);
        } else {
          setAdminColis([]);
        }
      } else {
        setColis([]);
        setMyColisIdsState([]);
        setAdminColis([]);
      }
    } catch {
      setUserState(null);
      setColis([]);
      setMyColisIdsState([]);
      setAdminColis([]);
    } finally {
      setAuthBooting(false);
    }
  }, [apiRequest, fetchCsrf]);

  const refreshSession = useCallback(async () => {
    const json = await apiRequest('me');
    const u = json?.user || null;
    setUserState(u);
    return u;
  }, [apiRequest]);

  const refreshColis = useCallback(async () => {
    if (!user) {
      setColis([]);
      setMyColisIdsState([]);
      setAdminColis([]);
      return [];
    }
    const json = await apiRequest('colis');
    const list = Array.isArray(json?.colis) ? json.colis : [];
    setColis(list);
    setMyColisIdsState(list.map(c => c.id));

    if (user?.is_admin) {
      const adminJson = await apiRequest('admin/colis');
      const adminList = Array.isArray(adminJson?.colis) ? adminJson.colis : [];
      setAdminColis(adminList);
    }
    return list;
  }, [apiRequest, user]);

  const refreshAdminColis = useCallback(async () => {
    if (!user?.is_admin) {
      setAdminColis([]);
      return [];
    }
    const json = await apiRequest('admin/colis');
    const list = Array.isArray(json?.colis) ? json.colis : [];
    setAdminColis(list);
    return list;
  }, [apiRequest, user]);

  const adminUpdateColis = useCallback(async (id, patch = {}) => {
    if (!user?.is_admin) {
      throw new Error('Action réservée à un administrateur');
    }

    await apiRequest(`admin/colis/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch || {}),
    });

    await refreshAdminColis();
    await refreshColis();
  }, [apiRequest, refreshAdminColis, refreshColis, user]);

  useEffect(() => {
    bootstrapAuth();
  }, [bootstrapAuth]);

  const register = useCallback(async (name, email, password, tel) => {
    const n = (name || '').trim();
    const e = (email || '').trim().toLowerCase();
    const p = (password || '').trim();
    const t = (tel || '').trim();
    if (!n || !e || !p || !t) throw new Error('Nom, email, téléphone et mot de passe requis');

    const json = await apiRequest('register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: n, email: e, password: p, phone: t }),
    });

    const u = json?.user || null;
    showToast('Compte créé avec succès ✅', 'success');
    return u;
  }, [apiRequest, refreshColis, showToast]);

  const login = useCallback(async (email, password) => {
    const e = (email || '').trim().toLowerCase();
    const p = (password || '').trim();
    if (!e || !p) throw new Error('Email et mot de passe requis');

    const json = await apiRequest('login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: e, password: p }),
    });

    const u = json?.user || null;
    setUserState(u);
    showToast('Connexion réussie ✅', 'success');
    await refreshColis();
    return u;
  }, [apiRequest, refreshColis, showToast]);

  const logout = useCallback(async () => {
    await apiRequest('logout', { method: 'POST' });
    setUserState(null);
    setColis([]);
    setMyColisIdsState([]);
    showToast('Déconnecté', 'info');
  }, [apiRequest, showToast]);

  const updateMe = useCallback(async (patch) => {
    const payload = {
      name: patch?.name,
      email: patch?.email,
      phone: patch?.phone,
    };

    const json = await apiRequest('me', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const u = json?.user || null;
    setUserState(u);
    showToast('Compte mis à jour ✅', 'success');
    return u;
  }, [apiRequest, showToast]);

  const addColis = useCallback(async (formData) => {
    const hasPhoto = !!formData?.photo && typeof formData.photo === 'object';

    let json;
    if (hasPhoto) {
      const payload = {
        nom: formData?.nom || '',
        tel: formData?.tel || '',
        destinataire: formData?.destinataire || '',
        adresse: formData?.adresse || '',
        zone: formData?.zone || '',
        statut: formData?.statut || 'depot',
        source: formData?.source || 'web',
        meta: formData?.meta || undefined,
        photo_base64: formData?.photo_base64 || '',
        photo_name: formData?.photo_name || (formData?.photo?.name || ''),
      };

      json = await apiRequest('colis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    } else {
      const payload = {
        nom: formData?.nom || '',
        tel: formData?.tel || '',
        destinataire: formData?.destinataire || '',
        adresse: formData?.adresse || '',
        zone: formData?.zone || '',
        statut: formData?.statut || 'depot',
        source: formData?.source || 'web',
        meta: formData?.meta || undefined,
      };

      json = await apiRequest('colis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    }
    const created = json?.colis;
    await refreshColis();
    showToast('Colis enregistré avec succès ! 📦', 'success');
    return created;
  }, [apiRequest, refreshColis, showToast]);

  const clearMyColis = useCallback(() => {
    setMyColisIdsState([]);
  }, []);

  // Mettre à jour le statut d'un colis
  const updateStatut = useCallback(async (id, statut, extra = {}) => {
    const isAdmin = !!user?.is_admin;
    const payload = { statut };
    if (Object.prototype.hasOwnProperty.call(extra, 'motif') || Object.prototype.hasOwnProperty.call(extra, 'motif_echec')) {
      payload.motif_echec = extra?.motif || extra?.motif_echec || null;
    }
    if (Object.prototype.hasOwnProperty.call(extra, 'livreur')) {
      payload.livreur = extra?.livreur || null;
    }
    const json = await apiRequest(isAdmin ? `admin/colis/${id}` : `colis/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (isAdmin) {
      await refreshAdminColis();
    }
    await refreshColis();
    return json?.colis || null;
  }, [apiRequest, refreshAdminColis, refreshColis, user]);

  // Affecter un livreur
  const affecterLivreur = useCallback(async (id, livreur) => {
    let updated = await updateStatut(id, 'affecte', { livreur });

    const token = updated?.meta?.live_token || '';
    if (!token && !!user?.is_admin) {
      const json = await apiRequest('admin/colis');
      const list = Array.isArray(json?.colis) ? json.colis : [];
      const found = list.find(c => String(c?.id) === String(id));
      if (found) updated = found;
    }

    showToast(`Colis affecté à ${livreur} 🛵`, 'success');
    return updated;
  }, [apiRequest, updateStatut, showToast, user]);

  // Mettre en route
  const mettreEnRoute = useCallback((id) => {
    updateStatut(id, 'en_route');
    showToast('Colis en route 🚚', 'success');
  }, [updateStatut, showToast]);

  // Marquer livré
  const marquerLivre = useCallback((id) => {
    updateStatut(id, 'livre');
    showToast('Colis livré avec succès 🎉', 'success');
  }, [updateStatut, showToast]);

  // Marquer échec
  const marquerEchec = useCallback((id, motif = '') => {
    updateStatut(id, 'echec', { motif: (motif || '').trim() });
    showToast('Échec de livraison signalé ❌', 'error');
  }, [updateStatut, showToast]);

  // Supprimer
  const supprimerColis = useCallback(async (id) => {
    const isAdmin = !!user?.is_admin;
    if (!isAdmin) {
      throw new Error('Seul un administrateur peut supprimer un colis');
    }
    await apiRequest(`admin/colis/${id}`, { method: 'DELETE' });
    if (isAdmin) {
      await refreshAdminColis();
    }
    await refreshColis();
    showToast('Colis supprimé', 'info');
  }, [apiRequest, refreshAdminColis, refreshColis, showToast, user]);

  // Stats calculées
  const computeStats = (list) => ({
    total:    list.length,
    depot:    list.filter(c => c.statut === 'depot').length,
    en_cours: list.filter(c => ['affecte', 'en_route'].includes(c.statut)).length,
    livre:    list.filter(c => c.statut === 'livre').length,
    echec:    list.filter(c => c.statut === 'echec').length,
    ca:       list
                .filter(c => c.statut === 'livre')
                .reduce((sum, c) => sum + (c.montant || 0), 0),
  });

  const stats = computeStats(colis);
  const adminStats = computeStats(adminColis);

  const value = {
    colis,
    stats,
    adminColis,
    adminStats,
    toast,
    myColisIds,
    user,
    authBooting,
    clientTel,
    setClientTel,
    showToast,
    dismissToast,
    addColis,
    clearMyColis,
    refreshAdminColis,
    adminUpdateColis,
    register,
    login,
    logout,
    updateMe,
    affecterLivreur,
    mettreEnRoute,
    marquerLivre,
    marquerEchec,
    supprimerColis,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}
